/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import * as React from "react";
import {
  Drawer,
  Typography,
  Grid,
  IconButton,
  Stack,
  TextField, 
  Tab,
  Divider, 
  LinearProgress,
  Box,
  CircularProgress,
  InputLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MapIcon from "@mui/icons-material/Map";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import BarChartIcon from "@mui/icons-material/BarChart";
import "../../../components/Drawers/DeviceDrawer.css";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { IFullDeviceDetails, INrCell, IRadioNodesDataGridRow } from "../../../store/api/deviceConfig/types";
import { setOpenRnCuDrawer } from "../../../store/api/devices/reducers";
import { useStoreDispatch } from "../../../store/hooks";
import { isOpenRnCuDrawerSelector, radioNodeSelector, selectedDeviceSelector } from "../../../store/api/devices/selectors";
import { useRNCuAppDrawer } from "./hooks";
import StatusIcon from "../../../components/Shared/Status/StatusIcon";
import { IBandData, IDuCellInfo } from "../../../store/api/devices/types";
import AddEditCuInformation from "./AddEditCuInformation";
import { DIALOGOPENFOR, PROVISION_CHECK_VAL } from "../../../globals/globals";
import { basicDialogSelector, confirmDialogSelector } from "../../../store/helpers/helperSelectors";
import { setBasicDialogOpenFor, setConfirmDialog } from "../../../store/helpers/helperReducers";
import AddEditRadioInfo from "./AddEditRadioInfo";
import ConfirmDialog from "../../../components/Shared/Dialogs/ConfirmDialog";
import { constStr } from "../../../globals/constants";
import { Sheet } from "../../../components/ui/sheet";
import { Button } from "../../../components/ui/button";
import { Checkbox } from "../../../components/ui/checkbox";
import { Label } from "../../../components/ui/label";
import { Loader2 } from "lucide-react";
import { InputField } from "../../../components/Shared/Radix/Inputs/InputField";
import { Tabs, TabsList, TabsTrigger } from "../../../components/ui/tabs";
import { setOpenRnDrawer } from "../../../store/api/devices/reducers";
import { iconButtonSxProps } from "../../../components/helpers";
import CheckboxLabel from "../../../components/Shared/CheckBox/CheckboxLabel";


interface IDeviceDrawerProps {
  data?: IRadioNodesDataGridRow;
  closedButtonClicked: (arg: boolean) => void;
  rowsData?: Array<IRadioNodesDataGridRow>;
  cellList?: INrCell | null;
  isCellDrawer?: string;
  boundRemote?: { 
    id: number; 
    label?: string; 
    floorPlanId: number; 
    floorName?: string;
    hardware?: { 
      manufacturer?: string; 
      model?: string; 
    };
    antenna?: {
      transmitterType?: string;
      azimuth?: number;
      mechanicalTilt?: number;
      mountOrientation?: number;
      height?: number;
      heightUnit?: string;
    };
  } | null;
  onUnbindRemote?: () => void;
  boundFloorData?: any | null;
  cellKpiData?: any[];
  onOpenPerformance?: (cellName: string) => void;
}

const CuDrawer: React.FC<IDeviceDrawerProps> = ({ data, closedButtonClicked, rowsData = [], cellList, isCellDrawer, boundRemote = null, onUnbindRemote, boundFloorData = null, cellKpiData, onOpenPerformance }) => {
  const dispatch = useStoreDispatch();
  const navigate = useNavigate();
  const {
    permissions,
    radioNodeFormData,
    setRadioNodeFormData,
    selectedRadio,
    setSelectedRadio,
    zones,
    setZones,
    zoneOption,
    cellFormDataOne,
    setCellFormDataOne,
    setCellFormDataTwo,
    isCreateCellRadioInfoOne,
    setIsCreateCellRadioInfoOne,
    setIsCreateCellRadioInfoTwo,
    handleSubmitRadioNode,
    isAddEditRadioOne,
    setIsAddEditRadioOne,
    handleDeleteRadioCell,
    onBlurHandler,
    errMsg,
    isLoadingConfigStatus,
    duBandData,
    setDuBandData,
    onBlurProvision,
    provisionIndex,
    setProvisionIndex,
    setRadioNodeList,
    nrCellRowsData,
    selectedBand,
    isLoadingCellInfo,
    selectedRadioNode,
    selectedBandVal,
    setSelectedBandVal,
    updateRnCellInfo,
    radioTypeCheck,
    setRadioTypeCheck,
    initialZones,
    provisionValid,
    handleSubmitProvisionData,
    setSelectedBand,
    handleRemoveAllCellsToZones,
    deviceDetail,
  } = useRNCuAppDrawer(data?.model || "", isCellDrawer || "");

  const toggleDrawer = (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (event.type === "keydown" && ((event as React.KeyboardEvent).key === "Tab" || (event as React.KeyboardEvent).key === "Shift")) {
      return;
    }
    dispatch(setOpenRnDrawer(open));
    closedButtonClicked(open);
  };

  const isOpenRnCuDrawer = useSelector(isOpenRnCuDrawerSelector);
  const basicDialogOpenFor = useSelector(basicDialogSelector);
  const selectedDevice: IFullDeviceDetails = useSelector(selectedDeviceSelector);
  const openConfirmDialog = useSelector(confirmDialogSelector);
  const radioNode: IRadioNodesDataGridRow = useSelector(radioNodeSelector);

  React.useEffect(() => {
    if (data) {
      setRadioNodeFormData(data);
    }
  }, [data, setRadioNodeFormData]);

  React.useEffect(() => {
    if (rowsData?.length > 0) {
      setRadioNodeList(rowsData);
    }
  }, [rowsData, setRadioNodeList]);

  const handleChange = (name: string, value: string) => {
    if (name === "provisionVal") {
      setProvisionIndex(Number(value));
    } else setRadioNodeFormData({ ...radioNodeFormData, [name]: value });
  };

  const handleSetSelectedZones = (newValue: any | null) => {
    setZones(newValue);
  };

  const handleTabChange = (newTab: string) => {
    setSelectedRadio(newTab);
  };
  const handleSelectBand = (val: string, type: string) => {
    if (type === "radio1") setDuBandData(val);
    else return;
  };

  const handleUpdateCellInfoOption = (newState: boolean, type: string) => {
    if (type === "radio1") setIsCreateCellRadioInfoOne(!newState);
    else setIsCreateCellRadioInfoTwo(!newState);
  };

  const handleUpdatedCellInfo = (formData: IDuCellInfo, type: string) => {
    if (type === "radio1") setCellFormDataOne(formData);
    else setCellFormDataTwo(formData);
  };

  const submitRadioNodeData = (type: string) => {
    if (type === "delete") {
      handleDeleteRadioCell();
    } else if (type === "provision") {
      handleSubmitProvisionData();
    } else handleSubmitRadioNode(type);
  };

  const handleAddRadioCellInfoOne = (cellId: any) => {
    setIsCreateCellRadioInfoOne(!isCreateCellRadioInfoOne);
    if (cellId) setIsAddEditRadioOne("edit");
    else setIsAddEditRadioOne("add");
  };

  const updateSelectedBandVal = (val: string) => {
    setSelectedBandVal(val);
  };

  const handleAddUpdateRnCellInfo = (cellId: any) => {
    const radiosCount = selectedRadioNode?.rnCellData?.radios;
    const handleRadioAction = (radioIndex: number, handleAddRadioCellInfo: (cellId: any) => void) => {
      if (radioNodeFormData?.radios[radioIndex]?.index === undefined) {
        setRadioTypeCheck("Add");
        selectedBandVal && updateRnCellInfo();
      } else if (radioNodeFormData?.radios[radioIndex]?.band?.toLowerCase() === "not-available") {
        setRadioTypeCheck("Edit");
        selectedBandVal && updateRnCellInfo();
      } else if (radioNodeFormData?.radios[radioIndex]?.index && !cellId) {
        handleAddRadioCellInfo(cellId);
      }
    };

    if (radiosCount > 0) {
      if (selectedRadio === "radio1") {
        handleRadioAction(0, handleAddRadioCellInfoOne);
      } else return;
    }
  };

  const handleRnCellMsg = (cellId: any, isButton: boolean) => {
    let msg = "";
    const radioIndex = selectedRadio === "radio1" ? 0 : selectedRadio === "radio2" ? 1 : -1;
    const radiosCount = selectedRadioNode?.rnCellData?.radios;
    if (radiosCount > 0 && radioIndex >= 0) {
      const radioData = radioNodeFormData?.radios?.[radioIndex];
      if (radioData?.index === undefined) {
        msg = isButton ? "Add Radio" : `No radio has been added. Click "ADD RADIO" to add.`;
      } else if (radioData?.band?.toLowerCase() === "not-available") {
        msg = isButton ? "Update Radio" : `Band is not available. Please update radio from supported bandwidths. Click "UPDATE RADIO".`;
      } else {
        msg = isButton ? "Add Cell" : `No cells have been added. Click "ADD CELL" to add.`;
      }
    } else {
      msg = isButton ? "Add Cell" : `No cells have been added. Click "ADD CELL" to add.`;
    }
    return msg;
  };

  const handleRemovesZones = () => {
    handleRemoveAllCellsToZones();
  };

  const handleClosePopup = () => {
    dispatch(setConfirmDialog(undefined));
  };

  const handleSelect = (event: any) => {
    if (selectedRadio === "radio1") {
      setSelectedBand(event.target.value);
      setSelectedBandVal(event.target.value);
    } else return;
  };

  const handleIsCreateCell = () => {
    let isDisabled = false;
    if (selectedRadio === "radio1") {
      if (Array.isArray(radioNode?.radios) && radioNode?.radios?.length > 0 && radioNode?.radios[0].index && radioNode?.radios[0].index === "1") {
        if (radioNode.radios[0].cellId) {
          isDisabled = true;
        }
      }
    }
    return isDisabled;
  };

  const isBnadEnabled = (selectedBand: string, band: string) => {
    if (!handleIsCreateCell()) {
      return false;
    }
    if (selectedBand === band) {
      return false;
    } else return true;
  };

  const newFlatArray = (arr: string[]) => {
    return arr.flatMap((entry) => (entry?.includes("/") ? entry.split("/") : entry));
  };

  const showRadioBandData2 = () => {
    const AllBandData = selectedRadioNode?.bandData || [];
    const bandsArray =
      selectedRadio === "radio1" ? selectedRadioNode?.rnCellData?.radioBandOne || [] : selectedRadioNode?.rnCellData?.radioBandTwo || [];
    const totalNoOfBands = newFlatArray(bandsArray);
    let newArrayBand: IBandData[] = [];
    newArrayBand = AllBandData?.filter((band) => band && totalNoOfBands.includes(band.band));
    if (newArrayBand?.length > 0) {
      return newArrayBand?.map((bandVal: IBandData, index: number) => {
        const isDisabled = isLoadingConfigStatus || isBnadEnabled(selectedBand, bandVal?.bandValue);
        return (
          <label key={index} className={`flex items-center gap-1.5 text-sm ${isDisabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}>
            <input
              type="radio"
              name="bandSelection"
              value={bandVal?.bandValue}
              checked={selectedBand === bandVal?.bandValue}
              disabled={isDisabled}
              onChange={handleSelect}
              style={{ accentColor: "var(--primary)" }}
            />
            {bandVal?.band}
          </label>
        );
      });
    } else return;
  };

  const handleCancelRadioCell = (type: string) => {
    if (type === "radio1") {
      setIsCreateCellRadioInfoOne(false);
      setIsAddEditRadioOne("add");
    } else return;
  };

  const handleClose = () => {
    dispatch(setOpenRnCuDrawer(false));
    closedButtonClicked(false);
  };

  const isCellEditing = isCreateCellRadioInfoOne;

  const handleCellCancel = () => {
    handleCancelRadioCell(selectedRadio);
  };

  const footer = (
    <>
      <Button variant="outline" onClick={isCellEditing ? handleCellCancel : handleClose}>
        Cancel
      </Button>
      {permissions?.hasOpCodeGroup("manage_topology") && radioNodeFormData && radioNodeFormData?.id >= PROVISION_CHECK_VAL && (
        <Button
          disabled={isLoadingConfigStatus || deviceDetail?.configurationStatus === "OFFLINE"}
          onClick={handleSubmitProvisionData}
        >
          {isLoadingConfigStatus && <Loader2 size={16} className="animate-spin mr-2" />}
          Provision
        </Button>
      )}
      {permissions?.hasOpCodeGroup("manage_topology") && radioNodeFormData?.id < PROVISION_CHECK_VAL && (
        <Button
          disabled={radioNodeFormData?.id >= PROVISION_CHECK_VAL || isLoadingConfigStatus || selectedDevice?.status === 19 || deviceDetail?.configurationStatus === "OFFLINE"}
          onClick={() => handleSubmitRadioNode(isCellEditing ? isAddEditRadioOne : "edit")}
        >
          {isLoadingConfigStatus && <Loader2 size={16} className="animate-spin mr-2" />}
          Save
        </Button>
      )}
    </>
  );

  return (
    <>
      {basicDialogOpenFor === DIALOGOPENFOR.RNCUCONFIGS && (
        <AddEditRadioInfo
          onSubmitData={updateRnCellInfo}
          selectedBandVal={selectedBandVal}
          setSelectedBandVal={updateSelectedBandVal}
          configStatus={isLoadingConfigStatus}
          modelKey={data?.model || ""}
          selectedRadioNode={selectedRadioNode}
          cellType={selectedRadio}
          radioType={radioTypeCheck}
          radioIndex={selectedRadio === "radio1" ? "1" : selectedRadio === "radio2" ? "2" : ""}
        />
      )}
      {openConfirmDialog === DIALOGOPENFOR.RNCUPROVISION && (
        <ConfirmDialog actionName={constStr.ok} handleClickAction={handleClosePopup}>
          {provisionValid?.provMsg}
        </ConfirmDialog>
      )}
      <Sheet open={isOpenRnCuDrawer} onClose={handleClose} title={data?.name || "Unknown"} footer={footer} width="560px">
        <div className="space-y-5">
          <StatusIcon status={data?.status || ""} />

          <div className="flex items-center gap-2">
            <Checkbox id="cu-enable" disabled checked={radioNodeFormData?.enable || false} />
            <Label htmlFor="cu-enable">Enable</Label>
          </div>

          <InputField
            name="name"
            label="Name"
            disabled={(radioNodeFormData?.id >= PROVISION_CHECK_VAL) || selectedDevice?.configurationStatus === "OFFLINE"}
            value={radioNodeFormData?.name || ""}
            onChange={handleChange}
          />

          <InputField
            name="macAddress"
            label="MAC Address"
            disabled
            value={radioNodeFormData?.ethernetId || ""}
            onChange={handleChange}
          />

          <InputField
            name="description"
            label="Description"
            disabled={(radioNodeFormData?.id >= PROVISION_CHECK_VAL) || selectedDevice?.configurationStatus === "OFFLINE"}
            value={radioNodeFormData?.description || ""}
            onChange={handleChange}
          />

          {radioNodeFormData?.id >= PROVISION_CHECK_VAL && (
            <div>
              <InputField
                name="provisionVal"
                label="Index"
                type="number"
                required
                disabled={radioNodeFormData?.id < PROVISION_CHECK_VAL || isLoadingConfigStatus}
                value={provisionIndex}
                onChange={handleChange}
                onBlur={onBlurProvision}
              />
              {errMsg?.provisionVal && <p className="text-xs text-destructive mt-1">{errMsg.provisionVal}</p>}
            </div>
          )}

          {/* Radio Tabs */}
          {!isLoadingCellInfo && radioNodeFormData?.radios?.length > 0 && (
            <Tabs value={selectedRadio} onValueChange={handleTabChange}>
              <TabsList className="inline-flex h-auto gap-0 rounded-none border-0 bg-transparent p-0 text-muted-foreground shrink-0">
                <TabsTrigger
                  value="radio1"
                  disabled={isLoadingConfigStatus}
                  className="rounded-none border-b-2 border-transparent bg-transparent px-4 py-2 text-sm capitalize text-muted-foreground data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                >
                  Radio 1
                </TabsTrigger>
              </TabsList>
            </Tabs>
          )}

          {/* Band Selection */}
          {!isLoadingCellInfo && (
            <div className="space-y-2 pl-2">
              <Label>Band</Label>
              <div className="flex flex-wrap items-center gap-4">
                {showRadioBandData2()}
              </div>
            </div>
          )}

          <div className="h-px bg-border" />

          {/* Loading Cell Info */}
          {isLoadingCellInfo && (
            <div className="flex items-center gap-2">
              <Loader2 size={20} className="animate-spin text-primary" />
              <span className="text-base font-semibold text-foreground">Getting Cell Info...</span>
            </div>
          )}

          {/* Cell Information */}
          {!isLoadingCellInfo ? (
            selectedRadio === "radio1" ? (
              isCreateCellRadioInfoOne ? (
                <AddEditCuInformation
                  selectedRadioNode={radioNodeFormData}
                  cellFormData={cellFormDataOne}
                  setCellFormData={handleUpdatedCellInfo}
                  isCreateRadioCell={isCreateCellRadioInfoOne}
                  cellType={selectedRadio}
                  handleUpdateCellInfoOption={handleUpdateCellInfoOption}
                  zones={zones}
                  zoneOption={zoneOption}
                  selectedBand={selectedBand}
                  handleSelectBand={handleSelectBand}
                  handleSetSelectedZones={handleSetSelectedZones}
                  isAddEditType={isAddEditRadioOne}
                  handleSubmit={submitRadioNodeData}
                  onBlurHandler={onBlurHandler}
                  errMsg={errMsg}
                  configStatus={isLoadingConfigStatus}
                  bandData={duBandData}
                  nrCellListData={nrCellRowsData}
                  handleRemovesZones={handleRemovesZones}
                  initialZones={initialZones}
                  isProvisioned={radioNodeFormData?.id >= PROVISION_CHECK_VAL}
                  handleCancel={handleCancelRadioCell}
                  isCellDrawer={isCellDrawer}
                />
              ) : (
                permissions?.hasOpCodeGroup("manage_topology") &&
                !isCreateCellRadioInfoOne && (
                  <>
                    <div className="flex justify-between items-center">
                      <h3 className="text-lg font-semibold text-foreground">Cell Information</h3>
                      <Button
                        onClick={() =>
                          radioNodeFormData?.id >= PROVISION_CHECK_VAL
                            ? handleAddRadioCellInfoOne(cellFormDataOne?.cellId)
                            : handleAddUpdateRnCellInfo(cellFormDataOne?.cellId)
                        }
                        disabled={deviceDetail?.configurationStatus === "OFFLINE" || (radioNodeFormData?.id < PROVISION_CHECK_VAL ? false : selectedBand?.toLowerCase() === "not-available")}
                      >
                        {radioNodeFormData?.id >= PROVISION_CHECK_VAL ? "ADD CELL" : handleRnCellMsg(cellFormDataOne?.cellId, true)}
                      </Button>
                    </div>
                    <p className="text-sm" style={{ color: "var(--warning)" }}>
                      {radioNodeFormData?.id >= PROVISION_CHECK_VAL
                        ? `No cells have been added. Click "ADD CELL" to add.`
                        : handleRnCellMsg(cellFormDataOne?.cellId, false)}
                    </p>
                  </>
                )
              )
            ) : null
          ) : null}
          
        </div>
      </Sheet>
    </>
  );
};

export default CuDrawer;
